import { useCallback, useEffect, useState } from "react";
import { api, ApiError, browserTz, type DaySummary, type Meal } from "../lib/api";
import { MealCard } from "../components/MealCard";
import { EditMealDrawer } from "../components/EditMealDrawer";
import { MonthCalendar } from "../components/MonthCalendar";
import { Spinner } from "../components/Spinner";

function toLocalDateKey(date: Date): string {
  return new Intl.DateTimeFormat("en-CA").format(date);
}

function dayLabel(dateKey: string): string {
  const todayKey = toLocalDateKey(new Date());
  const yDt = new Date();
  yDt.setDate(yDt.getDate() - 1);
  const yesterdayKey = toLocalDateKey(yDt);
  if (dateKey === todayKey) return "Today";
  if (dateKey === yesterdayKey) return "Yesterday";
  const parts = dateKey.split("-");
  const y = parseInt(parts[0]!, 10);
  const m = parseInt(parts[1]!, 10);
  const d = parseInt(parts[2]!, 10);
  return new Date(y, m - 1, d, 12, 0, 0).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function groupByDay(meals: Meal[]): Array<{ dateKey: string; meals: Meal[] }> {
  const map = new Map<string, Meal[]>();
  for (const meal of meals) {
    const key = toLocalDateKey(new Date(meal.capturedAt as string | number));
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(meal);
  }
  return Array.from(map.entries()).map(([dateKey, meals]) => ({ dateKey, meals }));
}

type DaySectionProps = {
  dateKey: string;
  meals: Meal[];
  onEdit: (meal: Meal) => void;
  onDelete: (meal: Meal) => void;
};

function DaySection({ dateKey, meals, onEdit, onDelete }: DaySectionProps) {
  const [open, setOpen] = useState(true);
  const totalCal = meals.reduce((s, m) => s + m.calories, 0);
  const totalPro = meals.reduce((s, m) => s + m.proteinG, 0);
  const label = dayLabel(dateKey);

  return (
    <section className="day-section">
      <button
        className="day-section-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="day-label">{label}</span>
        <span className="day-meta">
          <span className="day-count">
            {meals.length} {meals.length === 1 ? "meal" : "meals"}
          </span>
          <span className="day-cal">{Math.round(totalCal)} kcal</span>
          <span className="day-pro">{Math.round(totalPro * 10) / 10} g protein</span>
        </span>
        <span className={`day-chevron${open ? " open" : ""}`} aria-hidden>
          ›
        </span>
      </button>
      {open && (
        <div className="day-meals">
          {meals.map((m) => (
            <MealCard
              key={m.id}
              meal={m}
              onEdit={() => onEdit(m)}
              onDelete={() => onDelete(m)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

type Mode = "recent" | "calendar";

function currentMonthStart(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

// Fixed floor rather than deriving from the earliest meal — avoids an extra lookup.
const MIN_MONTH = (() => {
  const c = currentMonthStart();
  return new Date(c.getFullYear(), c.getMonth() - 24, 1);
})();

export function Meals() {
  const [mode, setMode] = useState<Mode>("recent");
  const tz = browserTz();

  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Meal | null>(null);

  const [viewMonth, setViewMonth] = useState(currentMonthStart);
  const [selectedDay, setSelectedDay] = useState(() => toLocalDateKey(new Date()));
  const [monthDays, setMonthDays] = useState<DaySummary[]>([]);
  const [goal, setGoal] = useState({ calories: 2200, protein_g: 140 });
  const [dayMeals, setDayMeals] = useState<Meal[]>([]);
  const [calLoading, setCalLoading] = useState(true);
  const [calError, setCalError] = useState<string | null>(null);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.meals({ limit: 100 });
      setMeals(r.meals);
      setLoading(false);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
      setLoading(false);
    }
  }, []);

  const loadMonth = useCallback(async () => {
    setCalLoading(true);
    setCalError(null);
    try {
      const r = await api.summaryForMonth(viewMonth, tz);
      setMonthDays(r.days);
      setGoal(r.goal);
      setCalLoading(false);
    } catch (err) {
      setCalError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
      setCalLoading(false);
    }
  }, [viewMonth, tz]);

  const loadDay = useCallback(async () => {
    const parts = selectedDay.split("-").map(Number);
    const [y, m, d] = parts as [number, number, number];
    const from = new Date(y, m - 1, d, 0, 0, 0, 0);
    const to = new Date(y, m - 1, d, 23, 59, 59, 999);
    try {
      const r = await api.meals({ from, to });
      setDayMeals(r.meals);
    } catch (err) {
      setCalError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
    }
  }, [selectedDay]);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  useEffect(() => {
    loadDay();
  }, [loadDay]);

  const reload = useCallback(async () => {
    if (mode === "recent") {
      await loadRecent();
    } else {
      await Promise.all([loadDay(), loadMonth()]);
    }
  }, [mode, loadRecent, loadDay, loadMonth]);

  const clampedSetMonth = (d: Date) => {
    if (d < MIN_MONTH || d > currentMonthStart()) return;
    setViewMonth(d);
  };

  const handleDelete = async (m: Meal) => {
    if (!confirm("Delete this meal?")) return;
    await api.deleteMeal(m.id);
    await reload();
  };

  const groups = groupByDay(meals);
  const dayTotalCal = dayMeals.reduce((s, m) => s + m.calories, 0);
  const dayTotalPro = dayMeals.reduce((s, m) => s + m.proteinG, 0);

  return (
    <div className="meals-list">
      <div className="meals-mode-toggle">
        <button
          className={`meals-mode-toggle__btn${mode === "recent" ? " meals-mode-toggle__btn--active" : ""}`}
          onClick={() => setMode("recent")}
        >
          Recent
        </button>
        <button
          className={`meals-mode-toggle__btn${mode === "calendar" ? " meals-mode-toggle__btn--active" : ""}`}
          onClick={() => setMode("calendar")}
        >
          Calendar
        </button>
      </div>

      {mode === "recent" && (
        <>
          {loading && (
            <div className="loading">
              <Spinner />
            </div>
          )}
          {error && <div className="error">Failed to load meals — {error}</div>}
          {!loading && !error && (
            <>
              {meals.length === 0 && (
                <div className="empty">No meals yet. Send a photo via OpenClaw to get started.</div>
              )}
              {groups.map(({ dateKey, meals: dayMeals }) => (
                <DaySection
                  key={dateKey}
                  dateKey={dateKey}
                  meals={dayMeals}
                  onEdit={(m) => setEditing(m)}
                  onDelete={handleDelete}
                />
              ))}
            </>
          )}
        </>
      )}

      {mode === "calendar" && (
        <>
          {calError && <div className="error">Failed to load calendar — {calError}</div>}
          {!calError && (
            <>
              <MonthCalendar
                days={monthDays}
                month={viewMonth}
                onMonthChange={clampedSetMonth}
                minMonth={MIN_MONTH}
                onSelectDay={setSelectedDay}
                selectedDay={selectedDay}
                goal={goal}
                loading={calLoading}
              />
              <section className="day-view">
                <div className="day-view-header">
                  <span className="day-label">{dayLabel(selectedDay)}</span>
                  <span className="day-meta">
                    <span className="day-count">
                      {dayMeals.length} {dayMeals.length === 1 ? "meal" : "meals"}
                    </span>
                    <span className="day-cal">{Math.round(dayTotalCal)} kcal</span>
                    <span className="day-pro">{Math.round(dayTotalPro * 10) / 10} g protein</span>
                  </span>
                </div>
                {dayMeals.length === 0 ? (
                  <div className="empty">No meals logged this day.</div>
                ) : (
                  <div className="day-meals">
                    {dayMeals.map((m) => (
                      <MealCard
                        key={m.id}
                        meal={m}
                        onEdit={() => setEditing(m)}
                        onDelete={() => handleDelete(m)}
                      />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </>
      )}

      {editing && (
        <EditMealDrawer
          meal={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}
